import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createRouter, createWebHistory } from 'vue-router';
import App from './App.vue';
import './style.css';

// Import stores
import { useAuthStore } from './stores/auth';
import { storageService } from './services/storage';

// Import views
import AuthView from './views/AuthView.vue';
import DashboardView from './views/DashboardView.vue';
import ToolsView from './views/ToolsView.vue';
import ProfileView from './views/ProfileView.vue';
import TierProfileView from './views/TierProfileView.vue';
import PreferencesView from './views/PreferencesView.vue';
import ConversationsView from './views/ConversationsView.vue';
import ConversationDetailView from './views/ConversationDetailView.vue';
import NotFoundView from './views/NotFoundView.vue';
import EmailVerificationView from './views/EmailVerificationView.vue';

// Create router
const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'dashboard',
      component: DashboardView,
      meta: { requiresAuth: true },
    },
    {
      path: '/auth',
      name: 'auth',
      component: AuthView,
      meta: { requiresAuth: false },
      beforeEnter: (to, _from, next) => {
        // Handle authentication token from URL
        const token = to.query.token as string;
        if (token) {
          // Store token as persistent credential
          storageService.setAuthToken(token);
          // Redirect to auth page without token in URL
          next({ name: 'auth', query: {} });
        } else {
          next();
        }
      },
    },
    {
      path: '/tools',
      name: 'tools',
      component: ToolsView,
      meta: { requiresAuth: true },
    },
    {
      path: '/tools/:toolName',
      name: 'tool-detail',
      component: () => import('./views/ToolDetailView.vue'),
      meta: { requiresAuth: true },
      props: true,
    },
    {
      path: '/profile',
      name: 'profile',
      component: ProfileView,
      meta: { requiresAuth: true },
    },
    {
      path: '/profile/tier',
      name: 'profile-tier',
      component: TierProfileView,
      meta: { requiresAuth: true },
    },
    {
      path: '/profile/preferences',
      name: 'profile-preferences',
      component: PreferencesView,
      meta: { requiresAuth: true },
    },
    {
      path: '/conversations',
      name: 'conversations',
      component: ConversationsView,
      meta: { requiresAuth: true },
    },
    {
      path: '/conversations/:id',
      name: 'conversation-detail',
      component: ConversationDetailView,
      meta: { requiresAuth: true },
      props: true,
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: EmailVerificationView,
      meta: { requiresAuth: false },
    },
    {
      path: '/logout',
      name: 'logout',
      redirect: '/auth',
      beforeEnter: async (_to, _from, next) => {
        const authStore = useAuthStore();
        await authStore.logout(true); // Complete logout - clear persistent token
        next({ name: 'auth' });
      },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: NotFoundView,
    },
  ],
});

// Create Pinia store
const pinia = createPinia();

// Create Vue app
const app = createApp(App);

// Use plugins
app.use(pinia);
app.use(router);

// Global error handler
app.config.errorHandler = (err, instance, info) => {
  console.error('Global error:', err);
  console.error('Component instance:', instance);
  console.error('Error info:', info);

  // You could send this to a logging service
  // logError({ error: err, instance, info })
};

// Global warning handler
app.config.warnHandler = (msg, instance, trace) => {
  console.warn('Vue warning:', msg);
  console.warn('Component instance:', instance);
  console.warn('Component trace:', trace);
};

// Router guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  console.log(`Router guard: Navigating from ${from.path} to ${to.path}`);
  console.log(
    `Router guard: Auth initialized: ${authStore.isInitialized}, Authenticated: ${authStore.isAuthenticated}`
  );

  // Initialize auth store if not already initialized
  if (!authStore.isInitialized) {
    console.log('Router guard: Initializing auth store...');
    await authStore.initialize();
    console.log(
      `Router guard: Auth store initialized, authenticated: ${authStore.isAuthenticated}`
    );
  }

  // Special handling for auth page - don't redirect if already there
  if (to.name === 'auth') {
    console.log('Router guard: Navigating to auth page');
    // If already authenticated, redirect to intended route or dashboard
    if (authStore.isAuthenticated) {
      console.log('Router guard: User already authenticated, redirecting from auth page');
      const intendedRoute = storageService.consumeIntendedRoute();
      if (intendedRoute) {
        console.log('Router guard: Redirecting to intended route:', intendedRoute.path);
        next(intendedRoute);
      } else {
        console.log('Router guard: No intended route, redirecting to dashboard');
        next({ name: 'dashboard' });
      }
      return;
    }
    // Allow navigation to auth page if not authenticated
    console.log('Router guard: User not authenticated, allowing navigation to auth page');
    next();
    return;
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    console.log(
      'Router guard: Route requires auth but user not authenticated, redirecting to auth'
    );
    // Store the intended route for redirect after login
    storageService.storeCurrentAsIntended({
      path: to.path,
      query: to.query,
      params: to.params,
    });

    next({ name: 'auth' });
    return;
  }

  // Update last activity for authenticated users
  if (authStore.isAuthenticated) {
    authStore.updateLastActivity();
    console.log('Router guard: Updated last activity for authenticated user');
  }

  console.log('Router guard: Allowing navigation to', to.path);
  next();
});

// Handle route errors
router.onError(error => {
  console.error('Router error:', error);

  // Navigate to error page or home
  router.push({ name: 'not-found' });
});

// Global properties
app.config.globalProperties.$appName = 'MCP Tools Collection';
app.config.globalProperties.$version = '1.0.0';

// Development mode configurations
if (import.meta.env.DEV) {
  app.config.performance = true;

  // Detailed logging in development
  console.log('🚀 MCP Tools Frontend starting in development mode');
  console.log('📝 Router routes:', router.getRoutes());
}

// Production mode configurations
if (import.meta.env.PROD) {
  // Remove console logs in production (optional)
  // console.log = () => {}
}

// Mount the app
app.mount('#app');

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', event => {
  console.error('Unhandled promise rejection:', event.reason);

  // Prevent the default browser behavior
  event.preventDefault();

  // You could send this to a logging service
  // logError({ error: event.reason, type: 'unhandledPromiseRejection' })
});

// Handle authentication token from URL (for initial login)
const urlParams = new URLSearchParams(window.location.search);
const authToken = urlParams.get('token');

if (authToken) {
  // Store the token for the auth store to process as persistent credential
  storageService.setAuthToken(authToken);

  // Clean the URL
  const cleanUrl = window.location.pathname + window.location.hash;
  window.history.replaceState({}, document.title, cleanUrl);
}

// Development utilities
if (import.meta.env.DEV) {
  // Global debug function for testing authentication
  (window as any).testAuth = {
    clearStorage: () => {
      localStorage.clear();
      console.log('All localStorage cleared');
    },
    setTestToken: (token: string) => {
      storageService.setAuthToken(token);
      console.log('Persistent test token set:', token.substring(0, 10) + '...');
    },
    getAuthState: () => {
      const authStore = useAuthStore();
      return {
        initialized: authStore.isInitialized,
        authenticated: authStore.isAuthenticated,
        user: authStore.user,
        hasToken: storageService.hasAuthToken(),
        hasSession: !!localStorage.getItem('mcp_session'),
      };
    },
    forceInitialize: async () => {
      const authStore = useAuthStore();
      await authStore.initialize();
      console.log('Auth store re-initialized');
    },
  };

  console.log('🔧 Debug utilities available: window.testAuth');
}

// Handle session timeout warnings
let sessionTimeoutWarning: number | null = null;

const showSessionTimeoutWarning = () => {
  const authStore = useAuthStore();

  if (authStore.isAuthenticated && !authStore.sessionTimeoutWarning) {
    authStore.setSessionTimeoutWarning(true);

    // Auto-logout after 5 minutes if no activity (but keep persistent token)
    sessionTimeoutWarning = window.setTimeout(
      () => {
        authStore.logout(false); // Don't clear persistent token
        router.push({ name: 'auth' });
      },
      5 * 60 * 1000
    );
  }
};

const hideSessionTimeoutWarning = () => {
  const authStore = useAuthStore();

  if (sessionTimeoutWarning) {
    clearTimeout(sessionTimeoutWarning);
    sessionTimeoutWarning = null;
  }

  authStore.setSessionTimeoutWarning(false);
};

// Listen for user activity to extend session
const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
const resetSessionTimeout = () => {
  hideSessionTimeoutWarning();

  const authStore = useAuthStore();
  if (authStore.isAuthenticated) {
    authStore.updateLastActivity();
  }
};

activityEvents.forEach(event => {
  document.addEventListener(event, resetSessionTimeout, { passive: true });
});

// Check for session timeout every minute
setInterval(() => {
  const authStore = useAuthStore();

  if (authStore.isAuthenticated && authStore.user) {
    const now = new Date();
    const expiresAt = new Date(authStore.user.expiresAt);
    const timeToExpiry = expiresAt.getTime() - now.getTime();

    // Show warning 10 minutes before expiry
    if (timeToExpiry <= 10 * 60 * 1000 && timeToExpiry > 0) {
      showSessionTimeoutWarning();
    }

    // Auto-logout if session expired (but keep persistent token for re-auth)
    if (timeToExpiry <= 0) {
      authStore.logout(false); // Don't clear persistent token
      router.push({ name: 'auth' });
    }
  }
}, 60 * 1000);

export { app, router, pinia };
